//#region Imports
import React, { useState, useEffect, useRef, Fragment } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import { bsaRischio } from "../../businessLogic/constants/securityAreas";
import { useAreaPermissions } from "../../businessLogic/hooks/useAreaPermissions";
import {
  TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
  TIPO_ANAGRAFICA_CLIENTE_PG_KEY,
} from "../../businessLogic/constants/baseTableKeys";

//oneFront SDK
import { useSnackbar, useQuery, useTranslation, useMutation } from "@1f/react-sdk";

import VaporPage from "@vapor/react-custom/VaporPage";

//Vapor Components
import Divider from "@vapor/react-material/Divider";
import Grid from "@vapor/react-material/Grid";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import { Button, Stack } from "@vapor/react-material";
import { Add } from "@mui/icons-material"
//Vapor Date Pickers
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { it } from "date-fns/locale";

//Constants, Api and Data Models
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import {
    rischioModel,
    prestazioneModel,
    baseRischioGradoModel,
} from "../../businessLogic/models";

import {
    query_rischi_byPrestazione,
} from "../../businessLogic/query/valutazioneRischioQueries";

import {
    query_prestazioni_byFascicolo,
} from "../../businessLogic/query/prestazioniQueries";

import {
    query_base_rischio_grado_byTenantTipo,
} from "../../businessLogic/query/baseTablesQueries";

import {
    mutation_add_rischio,
    mutation_update_rischio,
} from "../../businessLogic/mutations/valutazioniRischioMutations";

import {
    query_base_all
} from "../../businessLogic/query/baseTablesQueries";

//Other components
import { Loading } from "../Loading";
import { v4 as uuidv4 } from "uuid";
import { RischiList } from "./RischiList";
import { RischioDetail } from "./RischioDetail";
import { NoData } from "../NoData";
import { ModalDelete } from "../ModalDelete";
import { UserApi } from "../../businessLogic/api/userApi";
import { RischiApi } from "../../businessLogic/api/rischiApi";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
import { AutovalutazioneApi } from "../../businessLogic/api/autovalutazioneApi";
import { LogApi } from "../../businessLogic/api/logApi";
import { chipGreen, chipOrange, chipRed, chipYellow } from "../../businessLogic/styles";
import BaseTableApi from "../../businessLogic/api/baseTableApi";
import {
    getLocalStorageSelectedEntity,
    removeLocalStorageSelectedEntity,
} from "../../businessLogic/helpers/storageHelpers";


//#endregion

export const NewRischioForm = ({ fascicolo, reloadStatusFascicolo, prestazionePreselected = null }) => {
    const { enqueueSnackbar } = useSnackbar();
    const {t} = useTranslation()
    const tenantId = sharedDatas.getPropertyByName("tenantId");
    const aziendaId = sharedDatas.getPropertyByName("aziendaId");
    const { permissions, getPermissions } = useAreaPermissions(bsaRischio);
    const userApi = UserApi();
    const rischiApi = RischiApi();
    const fascicoloApi = FascicoloApi();
    const autovalutazioneApi = AutovalutazioneApi();
    const logApi = LogApi();
    const baseTableApi = BaseTableApi();
    //#region Query
    const apiRischiByPrestazione = useQuery(
        query_rischi_byPrestazione,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiPrestazioniByFascicolo = useQuery(
        query_prestazioni_byFascicolo,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiBaseRischioGradoByTenantTipo = useQuery(
        query_base_rischio_grado_byTenantTipo,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    const apiBaseAll = useQuery(
        query_base_all,
        {},
        { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
    );
    //#endregion

    //#region Mutations
    const apiAddRischio = useMutation(mutation_add_rischio, {
        graphqlEndpoint: "aml:hasura:api://v1/graphql",
    });
    const apiUpdateRischio = useMutation(mutation_update_rischio, {
        graphqlEndpoint: "aml:hasura:api://v1/graphql",
    });
    //#endregion

    //#region UseStates
    // Selezione proveniente dalla navigazione (RischioHome -> tipo "rischio").
    // Contiene { entityType:"rischio", entityId:<rischioId>, options:{ prestazioneId, providedBy } }.
    // La leggiamo una sola volta al mount e la teniamo nel ref per pilotare la
    // selezione a cascata prestazione -> professionista -> rischio.
    const navSelectionRef = useRef(getLocalStorageSelectedEntity());

    const [backDropOpen, setBackDropOpen] = useState(true);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    // Prestazioni
    const [prestazioniList, setPrestazioniList] = useState([]);
    const [selectedPrestazione, setSelectedPrestazione] = useState(prestazionePreselected);

    // Data di valutazione
    const [dataValutazione, setDataValutazione] = useState(selectedPrestazione?.dataStart || new Date());

    // Professionisti
    const [utentiList, setUtentiList] = useState([]);
    const [selectedProfessionista, setSelectedProfessionista] = useState(
        prestazionePreselected?.selectedProfessionista || null
    );

    // Rischi caricati
    const [rischiList, setRischiList] = useState([]);
    const [selectedRischio, setSelectedRischio] = useState(null);

    // Controlli UI
    const [isLoadingRischi, setIsLoadingRischi] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [reloadTrigger, setReloadTrigger] = useState(0);
    const [autoCreatedRischio, setAutoCreatedRischio] = useState(false); // Flag per evitare ricreazioni automatiche

    // Base Rischio Grado
    const [baseRischioGradoList, setBaseRischioGradoList] = useState([]);

    // Autovalutazione di studio (solo Consulenti del Lavoro): usata per precompilare la Tabella C
    // di una nuova valutazione. Letta in modo sincrono in createEmptyRischioModel.
    const studioAutovalRef = useRef(null);
    
    // Stato PEP fascicolo - per determinare se ci sono titolari effettivi PEP
    const [fascicoloHasTitEfPep, setFascicoloHasTitEfPep] = useState(false);
    
    //Rischio area geografica
    const [rischioPaeseCliente, setRischioPaeseCliente] = useState({
        paese: "Italia",
        value: 1,
        rischio: t("valutazione.form.rischioInerenteLivello1"),
        color: chipGreen,
    });
    const [rischioPaesePrest, setRischioPaesePrest] = useState({
        paese: "Italia",
        value: 1,
        rischio: t("valutazione.form.rischioInerenteLivello1"),
        color: chipGreen,
    });
    const [rischioProvinciaCliente, setRischioProvinciaCliente] = useState({
        provincia: "Milano",
        value: 1,
        rischio: t("valutazione.form.rischioInerenteLivello1"),
        color: chipGreen,
        found: false,
    });
    const [rischioProvinciaPrest, setRischioProvinciaPrest] = useState({
        provincia: "Milano",
        value: 1,
        rischio: t("valutazione.form.rischioInerenteLivello1"),
        color: chipGreen,
        found: false,
    });    
    //#endregion

    //#region UseEffects
    // Gestione rischio esistente per prestazione preselezionata
    useEffect(() => {
        if (prestazionePreselected?.rischioEsistente && rischiList.length > 0) {
            // Trova il rischio esistente nella lista e selezionalo
            const rischioEsistente = rischiList.find(r => 
                r.id === prestazionePreselected.rischioEsistente.id
            );
            if (rischioEsistente) {
                setSelectedRischio(rischioEsistente);
                setAutoCreatedRischio(true); // Marca come già gestito
            }
        } else if (prestazionePreselected && prestazionePreselected.id !== 0 && 
            !prestazionePreselected.rischioEsistente && 
            selectedPrestazione && selectedProfessionista && 
            isDataLoaded && !autoCreatedRischio && rischiList.length === 0 && permissions.canCreate) {
            // Crea automaticamente un nuovo rischio SOLO se:
            // - c'è una prestazione preselezionata
            // - non c'è un rischio esistente
            // - non ci sono rischi nella lista (primo accesso)
            // - non abbiamo già creato automaticamente un rischio
            const newRischio = createEmptyRischioModel();
            setSelectedRischio(newRischio);
            setAutoCreatedRischio(true); // Impostiamo il flag per evitare ricreazioni
            enqueueSnackbar("Nuova valutazione creata. Completa i dati e salva.", {
                variant: "success",
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prestazionePreselected, rischiList, selectedPrestazione, selectedProfessionista, isDataLoaded, autoCreatedRischio, permissions.canCreate]);
    
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setBackDropOpen(true);
                await loadPrestazioni();
                await loadBaseRischioGrado();
                await loadStudioAutovalutazione();
                setIsDataLoaded(true);
            } catch (error) {
                console.error("Errore nel caricamento dei dati iniziali:", error);
                enqueueSnackbar("Errore nel caricamento dei dati", {
                variant: "error",
                });
                logApi.AddLog(error.message,error.stack,"loadInitialData","NewRischioForm");
            } finally {
                setBackDropOpen(false);
            }
        };
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fascicolo]);

    // Carica i rischi quando cambiano i parametri di filtro
    useEffect(() => {
        if (selectedPrestazione && selectedProfessionista) {
            loadRischi();
            loadRischioAreaGeografica(selectedPrestazione)
        } else {
            setRischiList([]);
            setSelectedRischio(null);
            // NON resettiamo autoCreatedRischio qui per mantenere lo stato
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPrestazione, selectedProfessionista, reloadTrigger]);

    // Carica i professionisti quando cambia la prestazione selezionata
    useEffect(() => {
        if (selectedPrestazione) {
            loadProfessionisti();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPrestazione]);

    useEffect(() => {
        setDataValutazione(
            selectedPrestazione?.dataStart ? new Date(selectedPrestazione.dataStart) : new Date()
        );
    }, [selectedPrestazione]);

    //#region Preselezione da navigazione (RischioHome)
    // Consuma subito la selezione dal localStorage (resta nel ref) per evitare
    // che una selezione non risolta resti "appiccicata" ad aperture successive.
    useEffect(() => {
        if (navSelectionRef.current?.entityType === "rischio") {
            removeLocalStorageSelectedEntity();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 1) Seleziona la prestazione del rischio da aprire.
    useEffect(() => {
        const nav = navSelectionRef.current;
        if (nav?.entityType !== "rischio") return;
        const prestId = nav.options?.prestazioneId;
        if (prestId && prestazioniList.length > 0) {
            const target =
                prestazioniList.find((p) => p.id === prestId) ||
                prestazioniList.find((p) => String(p.id) === String(prestId));
            if (target) setSelectedPrestazione(target);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prestazioniList]);

    // 2) Seleziona il professionista che ha fornito il rischio (i rischi sono
    //    filtrati per professionista, quindi senza questo il rischio non comparirebbe).
    useEffect(() => {
        const nav = navSelectionRef.current;
        if (nav?.entityType !== "rischio") return;
        const providedBy = nav.options?.providedBy;
        if (providedBy && utentiList.length > 0) {
            const target = utentiList.find((u) => u.userId === providedBy);
            if (target) setSelectedProfessionista(target);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [utentiList]);

    // 3) Selezionato prestazione + professionista, i rischi sono caricati: trova
    //    e seleziona quello richiesto, poi consuma la selezione (una sola volta).
    useEffect(() => {
        const nav = navSelectionRef.current;
        if (nav?.entityType !== "rischio" || !nav.entityId) return;
        if (rischiList.length > 0) {
            const target =
                rischiList.find((r) => r.id === nav.entityId) ||
                rischiList.find((r) => String(r.id) === String(nav.entityId));
            if (target) {
                setSelectedRischio(target);
                setAutoCreatedRischio(true);
                navSelectionRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rischiList]);
    //#endregion
    //#endregion

    //#region Load Methods
    // Precarica l'ultima autovalutazione di studio per i Consulenti del Lavoro (tenantTipoId === 2),
    // così da poter proporre i suoi valori Ri1/Ri2/Ri3 nella Tabella C di una nuova valutazione.
    // In assenza di autovalutazione di studio si mantiene il default (1) sulla Tabella C.
    const loadStudioAutovalutazione = async () => {
        studioAutovalRef.current = null;
        try {
            const userModel = sharedDatas.getPropertyByName("userModel");
            if (!userModel || userModel.tenantTipoId !== 2) return;

            const aziendaId = sharedDatas.getPropertyByName("aziendaId");
            if (!aziendaId) return;

            const lastAutoval = await autovalutazioneApi.GetLastAutovalutazione(tenantId, aziendaId);
            if (lastAutoval) {
                studioAutovalRef.current = lastAutoval;
            }
        } catch (error) {
            // Fallback silenzioso: la Tabella C resterà ai valori di default (1)
            console.error("Errore nel caricamento dell'autovalutazione di studio:", error);
            logApi.AddLog(error.message, error.stack, "loadStudioAutovalutazione", "NewRischioForm");
        }
    };

    const loadPrestazioni = async () => {
        try {
        if (!fascicolo || fascicolo.id <= 0) return;

        // Ottieni il tipo di tenant dell'utente corrente
        const userModel = sharedDatas.getPropertyByName("userModel");
        if (!userModel || !userModel.tenantTipoId) {
            console.error("User model o tenantTipoId non disponibile");
            return;
        }

        const response = await apiPrestazioniByFascicolo.fetch({
            fascicoloId: fascicolo.id,
        });

        if (response.data.errors) {
            console.error(
            "Errore nel caricamento prestazioni:",
            response.data.errors
            );
            logApi.AddLog(response.data.errors,"loadPrestazioni","NewRischioForm");
            return;
        }

        // Filtra le prestazioni in base al tipo di tenant dell'utente
        const prestazioniFiltered = response.data.data.prestazione.filter((item) => {
            return item.base_tipo_prestazione.BaseTenantTipoId === userModel.tenantTipoId;
        });

        const prestazioni = prestazioniFiltered.map((item) => {
            let prefixPrest = "";
            if (item.base_tipo_prestazione.BaseTenantTipoId === 1) {
            prefixPrest = "COMM";
            } else if (item.base_tipo_prestazione.BaseTenantTipoId === 2) {
            prefixPrest = "CONS";
            } else if (item.base_tipo_prestazione.BaseTenantTipoId === 3) {
            prefixPrest = "AVV";
            } else if (item.base_tipo_prestazione.BaseTenantTipoId === 4) {
            prefixPrest = "NOT";
            }
            const prestazione = new prestazioneModel(
            item.Id,
            item.TipoKey,
            item.PrestazioneId,
            prefixPrest + " - " + item.base_tipo_prestazione.Nome,
            item.base_tipo_prestazione.Rischio,
            item.base_tipo_prestazione.Regole,
            item.StartDate,
            item.EndDate,
            item.PaeseId,
            item.ProvinciaId,
            item.Provincia,
            item.CittaId,
            item.Citta,
            item.ScopoId,
            item.NaturaId,
            item.Descrizione,
            item.IsPrivate,
            item.CreatedOn,
            item.ModifiedOn,
            [],
            [],
            item.AstensioneMotivo,
            item.IsEsoneroObbAst,
            "",
            "",
            [],
            item.base_tipo_prestazione.BaseTenantTipoId,
            item.CreatedBy,
            item.ModifiedBy
            );
            
            // Aggiungi i dati degli utenti associati alla prestazione
            prestazione.prestazione_users = item.prestazione_users || [];
            
            return prestazione;
        });

        setPrestazioniList(prestazioni);
        if (prestazioni.length > 0) {
            // Se non c'è una prestazione preselezionata (né da prop né da
            // navigazione RischioHome), seleziona la prima.
            const navPrestId =
                navSelectionRef.current?.entityType === "rischio"
                    ? navSelectionRef.current?.options?.prestazioneId
                    : null;
            if ((!prestazionePreselected || !prestazionePreselected.id) && !navPrestId) {
                setSelectedPrestazione(prestazioni[0]);
            }
        } else {
            // Se non ci sono prestazioni, assicurati che sia null
            setSelectedPrestazione(null);
            enqueueSnackbar("Nessuna prestazione trovata per questo fascicolo", {
                variant: "warning",
            });
        }
        } catch (error) {
        console.error("Errore nel caricamento prestazioni:", error);
        enqueueSnackbar("Errore nel caricamento delle prestazioni", {
            variant: "error",
        });
        logApi.AddLog(error.message,error.stack,"loadPrestazioni","NewRischioForm");
        }
    };

    const loadProfessionisti = async () => {
        try {
            if (!selectedPrestazione || selectedPrestazione.id <= 0) {
                setUtentiList([]);
                setSelectedProfessionista(null);
                return;
            }            
            // I professionisti sono già contenuti direttamente nella prestazione
            const professionistiAssociati = [];

            if (selectedPrestazione.professionisti && selectedPrestazione.professionisti.length > 0) {
                selectedPrestazione.professionisti.forEach(professionista => {
                    // I dati del professionista sono già completi nella struttura
                    const utenteNormalizzato = {
                        userId: professionista.userId,
                        name: professionista.name,
                        surname: professionista.surname,
                        completeName: professionista.completeName || `${professionista.name} ${professionista.surname}`.trim(),
                        email: professionista.email,
                        username: professionista.username,
                        type: professionista.type,
                        tenantTipoId: professionista.tenantTipoId,
                        isActive: professionista.isActive
                    };
                    professionistiAssociati.push(utenteNormalizzato);
                });
            } else if (selectedPrestazione.prestazione_users && selectedPrestazione.prestazione_users.length > 0) {
                let allUtenti = await userApi.GetUsersByCompanyId();
                selectedPrestazione.prestazione_users.forEach(prestUser => {
                    const utente = allUtenti.find(u => u.userId === prestUser.UserId);
                    if (utente) {
                        // Assicuriamoci che l'oggetto utente abbia tutte le proprietà necessarie
                        const utenteNormalizzato = {
                            ...utente,
                            completeName: utente.completeName || utente.name || utente.userId || '',
                            name: utente.name || utente.completeName || utente.userId || '',
                            userId: utente.userId || ''
                        };
                        professionistiAssociati.push(utenteNormalizzato);
                    }
                });
            }
            
            setUtentiList(professionistiAssociati);
            
            if (professionistiAssociati.length > 0) {
                // Seleziona automaticamente il primo professionista SOLO se c'è una prestazione preselezionata
                // oppure se c'è un professionista specifico preselezionato
                if (prestazionePreselected?.selectedProfessionista) {
                    // Se c'è un professionista specifico preselezionato, usalo
                    const professionistaPreselezionato = professionistiAssociati.find(
                        p => p.userId === prestazionePreselected.selectedProfessionista.userId
                    );
                    if (professionistaPreselezionato) {
                        setSelectedProfessionista(professionistaPreselezionato);
                    } else {
                        setSelectedProfessionista(professionistiAssociati[0]);
                    }
                } else if (prestazionePreselected && !prestazionePreselected.selectedProfessionista) {
                    // Se c'è una prestazione preselezionata ma nessun professionista specifico, seleziona il primo
                    setSelectedProfessionista(professionistiAssociati[0]);
                }
                // Se non ci sono preselezioni, non selezionare automaticamente nessun professionista
            } else {
                setSelectedProfessionista(null);
                enqueueSnackbar("Nessun professionista associato a questa prestazione", {
                    variant: "warning",
                });
            }
        } catch (error) {
            console.error("Errore nel caricamento professionisti:", error);
            enqueueSnackbar("Errore nel caricamento dei professionisti", {
                variant: "error",
            });
            logApi.AddLog(error.message,error.stack,"loadProfessionisti","NewRischioForm");
        }
    };

    const loadRischi = async () => {
        try {
            if (!selectedPrestazione) return;

            setIsLoadingRischi(true);
            const response = await apiRischiByPrestazione.fetch({
                prestazioneId: selectedPrestazione.id,
            });

            if (response.data.errors) {
                console.error("Errore nel caricamento rischi:", response.data.errors);
                setRischiList([]);
                logApi.AddLog(response.data.errors,"loadRischi","NewRischioForm");
                return;
            }

            const rischi = (response.data.data.rischio || []).map((item) =>
                new rischioModel(
                    item.Id,
                    item.Date,
                    selectedPrestazione,
                    item.SpA1Livello,
                    item.SpA1_1,
                    item.SpA1_2,
                    item.SpA1_3,
                    item.SpA1_4,
                    item.SpA1_5,
                    item.SpA1_6,
                    item.SpA1_7,
                    item.SpA1Altro,
                    item.SpA1AltroNote,
                    item.SpA2Livello,
                    item.SpA2_1,
                    item.SpA2_2,
                    item.SpA2_3,
                    item.SpA2Altro,
                    item.SpA2AltroNote,
                    item.SpA3Livello,
                    item.SpA3_1,
                    item.SpA3_2,
                    item.SpA3_3,
                    item.SpA3_4,
                    item.SpA3Altro,
                    item.SpA3AltroNote,
                    item.SpA4Livello,
                    item.SpA4_1,
                    item.SpA4_2,
                    item.SpA4_3,
                    item.SpA4_4,
                    item.SpA4Altro,
                    item.SpA4AltroNote,
                    item.SpASubTotale,
                    item.SpB1Livello,
                    item.SpB1_1,
                    item.SpB1_2,
                    item.SpB1_3,
                    item.SpB1_4,
                    item.SpB1Altro,
                    item.SpB1AltroNote,
                    item.SpB2Livello,
                    item.SpB2_1,
                    item.SpB2_2,
                    item.SpB2_3,
                    item.SpB2_4,
                    item.SpB2Altro,
                    item.SpB2AltroNote,
                    item.SpB3Livello,
                    item.SpB3_1,
                    item.SpB3_2,
                    item.SpB3_3,
                    item.SpB3Altro,
                    item.SpB3AltroNote,
                    item.SpB4Livello,
                    item.SpB4_1,
                    item.SpB4_2,
                    item.SpB4_3,
                    item.SpB4Altro,
                    item.SpB4AltroNote,
                    item.SpB5Livello,
                    item.SpB5_1,
                    item.SpB5_2,
                    item.SpB5_3,
                    item.SpB5Altro,
                    item.SpB5AltroNote,
                    item.SpB6Livello,
                    item.SpB6_1,
                    item.SpB6_2,
                    item.SpB6_3,
                    item.SpB6_4,
                    item.SpB6Altro,
                    item.SpB6AltroNote,
                    item.SpB7Livello || 1,
                    item.SpBSubTotale,
                    item.SpC1_1_Livello,
                    item.SpC1_2_Livello,
                    item.SpC1_3_Livello,
                    item.SpCSubTotale,
                    item.EfRiTotale,
                    item.EfSpTotale,
                    item.EfTotale,
                    item.EfAdeguataLivello,
                    item.HasTabellaB,
                    item.ProvidedBy,
                    item.CreatedOn ? new Date(item.CreatedOn) : null,
                    item.ModifiedOn ? new Date(item.ModifiedOn) : null,
                    "",
                    "",
                    "",
                    item.DataControlloCostante
                    ? new Date(item.DataControlloCostante)
                    : null,
                    item.CreatedBy,
                    item.ModifiedBy,
                    null,
                    item.HasTitEffPep
                )
            );

        // Filtra i rischi in base alla data di valutazione e al professionista selezionato
        const rischiFiltrati = rischi.filter((rischio) => {
            const profMatch =
            !selectedProfessionista ||
            rischio.providedBy === selectedProfessionista.userId;
            return profMatch;
        });

        setRischiList(rischiFiltrati);

        if (rischiFiltrati.length === 0) {
            setSelectedRischio(null);
        }   
        
        let hasTitEfPepInFascicolo = false;
        if (fascicolo && fascicolo.anagraficaFascicoli && fascicolo.anagraficaFascicoli.length > 0) {
            for (const anagFasc of fascicolo.anagraficaFascicoli) {
                if (anagFasc.anagrafica && anagFasc.anagrafica.anagrafica_fascicolos) {                    
                    const hasTitolareEffettivoRole = anagFasc.anagrafica.anagrafica_fascicolos.some(
                        fascicoloRel => fascicoloRel.TipoAnagId === 5 && fascicoloRel.FascicoloId === fascicolo.id
                    );
                    
                    if (hasTitolareEffettivoRole) {                        
                        if (anagFasc.anagrafica.IsPep === true) {
                            hasTitEfPepInFascicolo = true;
                            break;
                        }
                    }
                }
            }
        }
        setFascicoloHasTitEfPep(hasTitEfPepInFascicolo);
        
    } catch (error) {
        console.error("Errore nel caricamento rischi:", error);
        enqueueSnackbar("Errore nel caricamento dei rischi", {
            variant: "error",
        });
        logApi.AddLog(error.message,error.stack,"loadRischi","NewRischioForm");
        setRischiList([]);
        } finally {
        setIsLoadingRischi(false);
    }
    };

    const loadBaseRischioGrado = async () => {
        try {
            const userModel = sharedDatas.getPropertyByName("userModel");
            if (!userModel || !userModel.tenantTipoId) return;

            const response = await apiBaseRischioGradoByTenantTipo.fetch({
                tenantTipoId: userModel.tenantTipoId,
            });

            if (response.data.errors) {
                console.error("Errore nel caricamento base rischio grado:", response.data.errors);
                logApi.AddLog(response.data.errors,"loadBaseRischioGrado","NewRischioForm");
                return;
            }

            const baseRischioGrado = (response.data.data.base_rischio_grado || []).map((item) =>
                new baseRischioGradoModel(
                    item.Id,
                    item.TipoRischio,
                    item.GradoRischio,
                    item.AdeguataVerifica,
                    item.ValoreRischioDa,
                    item.ValoreRischioA,
                    item.Colore,
                    item.Offset
                )
            );
            
            setBaseRischioGradoList(baseRischioGrado);
        } catch (error) {
            console.error("Errore nel caricamento base rischio grado:", error);
            enqueueSnackbar("Errore nel caricamento della base rischio grado", {
                variant: "error",
            });
            logApi.AddLog(error.message,error.stack,"loadBaseRischioGrado","NewRischioForm");
        }
    };

    async function loadRischioAreaGeografica(prestItem,clienteItem)  {
        try {   
            // Verifica che fascicolo e anagraficaFascicoli siano definiti
            if (!fascicolo || !fascicolo.anagraficaFascicoli || fascicolo.anagraficaFascicoli.length === 0) {
                console.warn("Fascicolo o anagrafiche del fascicolo non disponibili");
                return;
            }
            
            // Verifica che prestItem sia definito
            if (!prestItem) {
                console.warn("Prestazione non disponibile per il calcolo del rischio area geografica");
                return;
            }
            
            let baseAllResponse = await apiBaseAll.fetch({ tenantId });
            let province = baseTableApi.GetBaseProvincia(baseAllResponse)
            let paesi = baseTableApi.GetBasePaese(baseAllResponse)
            
            // Verifica che paesi e province siano definiti e siano array
            if (!paesi || !Array.isArray(paesi) || paesi.length === 0) {
                console.warn("Lista paesi non disponibile");
                return;
            }
            
            if (!province || !Array.isArray(province)) {
                console.warn("Lista province non disponibile");
                return;
            }
            
            clienteItem = fascicolo.anagraficaFascicoli.find(af => af?.base_tipo_anagrafica?.Key === TIPO_ANAGRAFICA_CLIENTE_PF_KEY || af?.base_tipo_anagrafica?.Key === TIPO_ANAGRAFICA_CLIENTE_PG_KEY)?.anagrafica;
            
            if (!clienteItem) {
                console.warn("Cliente non trovato nel fascicolo");
                enqueueSnackbar("Cliente non trovato nel fascicolo", { variant: "warning" });
                return;
            }
            
            //Rischio area geografica cliente
            const paeseCliente = paesi.filter(
                (x) => x.id === clienteItem.AddrPaeseId
            )[0];
            
            if (!paeseCliente) {
                console.warn("Paese cliente non trovato");
                enqueueSnackbar("Paese cliente non trovato", { variant: "warning" });
                return;
            }
            
            const paeseClienteS = paeseCliente.isRischio 
                ? t("valutazione.form.rischioInerenteLivello4")
                : t("valutazione.form.rischioInerenteLivello1");
            const paeseClienteC = paeseCliente.isRischio ? chipRed : chipGreen;
            const paeseClienteV = paeseCliente.isRischio ? 4 : 1;
    
            setRischioPaeseCliente((prevState) => ({
                ...prevState,
                paese: paeseCliente.nome,
                rischio: paeseClienteS,
                color: paeseClienteC,
                value: paeseClienteV,
            }));
    
            if (paeseCliente.identificativo === "ITA") {
                const provinciaCliente = province.filter(
                    (x) => x.id === clienteItem.AddrProvinciaId
                )[0];
                
                if (!provinciaCliente) {
                    console.warn("Provincia cliente non trovata");
                    // Non mostrare il badge della provincia se non trovata
                    setRischioProvinciaCliente((prevState) => ({
                        ...prevState,
                        found: false,
                    }));
                } else {
                    let provinciaClienteS = t("valutazione.form.rischioInerenteLivello1");
                    let provinciaClienteC = chipGreen;
                    let provinciaClienteV = 1;
        
                    // Valida che rischio sia definito
                    if (provinciaCliente.rischio !== undefined && provinciaCliente.rischio !== null) {
                        provinciaClienteV = provinciaCliente.rischio;
                        switch (provinciaCliente.rischio) {
                            case 1: {
                                provinciaClienteS = t("valutazione.form.rischioInerenteLivello1");
                                provinciaClienteC = chipGreen;
                                break;
                            }
                            case 2: {
                                provinciaClienteS = t("valutazione.form.rischioInerenteLivello2");
                                provinciaClienteC = chipYellow;
                                break;
                            }
                            case 3: {
                                provinciaClienteS = t("valutazione.form.rischioInerenteLivello3");
                                provinciaClienteC = chipOrange;
                                break;
                            }
                            case 4: {
                                provinciaClienteS = t("valutazione.form.rischioInerenteLivello4");
                                provinciaClienteC = chipRed;
                                break;
                            }
                            default: {
                                break;
                            }
                        }
                    }
        
                    setRischioProvinciaCliente((prevState) => ({
                    ...prevState,
                    provincia: provinciaCliente.nome,
                    rischio: provinciaClienteS,
                    color: provinciaClienteC,
                    value: provinciaClienteV,
                    found: true,
                    }));
                }
            }
    
            //Rischio area geografica prestazione
            const paesePrest = paesi.filter((x) => x.id === prestItem.paeseId)[0];
            
            if (!paesePrest) {
                console.warn("Paese prestazione non trovato");
                enqueueSnackbar("Paese prestazione non trovato", { variant: "warning" });
                return;
            }
            
            const paesePrestS = paesePrest.isRischio
                ? t("valutazione.form.rischioInerenteLivello4")
                : t("valutazione.form.rischioInerenteLivello1");
            const paesePrestC = paesePrest.isRischio ? chipRed : chipGreen;
            const paesePrestV = paesePrest.isRischio ? 4 : 1;
    
            if (paesePrest.identificativo === "ITA") {
                const provinciaPrest = province.filter(
                    (x) => x.id === prestItem.provinciaId
                )[0];
                
                if (!provinciaPrest) {
                    console.warn("Provincia prestazione non trovata");
                    // Non mostrare il badge della provincia se non trovata
                    setRischioProvinciaPrest((prevState) => ({
                        ...prevState,
                        found: false,
                    }));
                } else {
                    let provinciaPrestS = t("valutazione.form.rischioInerenteLivello1");
                    let provinciaPrestC = chipGreen;
                    let provinciaPrestV = 1;
                    
                    // Valida che rischio sia definito
                    if (provinciaPrest.rischio !== undefined && provinciaPrest.rischio !== null) {
                        provinciaPrestV = provinciaPrest.rischio;
                        switch (provinciaPrest.rischio) {
                            case 1: {
                                provinciaPrestS = t("valutazione.form.rischioInerenteLivello1");
                                provinciaPrestC = chipGreen;
                                break;
                            }
                            case 2: {
                                provinciaPrestS = t("valutazione.form.rischioInerenteLivello2");
                                provinciaPrestC = chipYellow;
                                break;
                            }
                            case 3: {
                                provinciaPrestS = t("valutazione.form.rischioInerenteLivello3");
                                provinciaPrestC = chipOrange;
                                break;
                            }
                            case 4: {
                                provinciaPrestS = t("valutazione.form.rischioInerenteLivello4");
                                provinciaPrestC = chipRed;
                                break;
                            }
                            default: {
                                break;
                            }
                        }
                    }

                    setRischioProvinciaPrest((prevState) => ({
                        ...prevState,
                        provincia: provinciaPrest.nome,
                        rischio: provinciaPrestS,
                        color: provinciaPrestC,
                        value: provinciaPrestV,
                        found: true,
                    }));
                }
            }
    
            setRischioPaesePrest((prevState) => ({
                ...prevState,
                paese: paesePrest.nome,
                rischio: paesePrestS,
                color: paesePrestC,
                value: paesePrestV,
            }));
        } catch (e) {
            logApi.AddLog(e?.message || String(e), e?.stack || "", "LoadRischioAreaGeografica", "NewRischioForm");      
        } finally {

        }
    };
    // Funzione per calcolare la descrizione dell'adeguata verifica
    const getAdeguateVerificationRules = (totaleRischioEffettivo, isPep) => {
        if (baseRischioGradoList.length === 0) return "";

        const userModel = sharedDatas.getPropertyByName("userModel");
        if (!userModel || userModel.tenantTipoId === 3) return "";

        let baseGradoRischioItem = null;

        if (totaleRischioEffettivo >= 1.0 && totaleRischioEffettivo <= 1.5) {
            baseGradoRischioItem = baseRischioGradoList.find(
                (x) => x.valoreRischioDa === 1.0 && x.valoreRischioA === 1.5
            );
        } else if (totaleRischioEffettivo > 1.5 && totaleRischioEffettivo <= 2.5) {
            baseGradoRischioItem = baseRischioGradoList.find(
                (x) => x.valoreRischioDa === 1.5 && x.valoreRischioA === 2.5
            );
        } else if (totaleRischioEffettivo > 2.5 && totaleRischioEffettivo <= 3.5) {
            baseGradoRischioItem = baseRischioGradoList.find(
                (x) => x.valoreRischioDa === 2.5 && x.valoreRischioA === 3.5
            );
        } else if (totaleRischioEffettivo > 3.5) {
            baseGradoRischioItem = baseRischioGradoList.find(
                (x) => x.valoreRischioDa === 3.5 && x.valoreRischioA === 4.0
            );
        }

        if(isPep) {
            baseGradoRischioItem = baseRischioGradoList.find(
                (x) => x.valoreRischioDa === 3.5 && x.valoreRischioA === 4.0
            );
        }          
        return baseGradoRischioItem
    };
    //#endregion

    //#region Handlers
    const handlePrestazioneChange = (event, newValue) => {
        setSelectedPrestazione(newValue);        
        setSelectedRischio(null);
        setAutoCreatedRischio(false); // Reset quando cambia la prestazione
    };

    const handleDataValutazioneChange = (newDate) => {
        if (newDate && isDateValid(newDate)) {
            setDataValutazione(newDate);
            
            // Se c'è un rischio selezionato, aggiorna anche la sua data
            if (selectedRischio) {
                setSelectedRischio(prev => ({
                    ...prev,
                    date: newDate
                }));
            }
        }
    };

    const handleProfessionistaChange = (event, newValue) => {
        setSelectedProfessionista(newValue);
        setSelectedRischio(null);
        setAutoCreatedRischio(false);
    };

    const handleRischioSelect = (rischio) => {
        setSelectedRischio(rischio);
        setDataValutazione(new Date(rischio.date));
    };

    const handleRischioDelete = (rischio) => {
        if (!getPermissions().canDelete) return;
        if(rischio){
            setShowDeleteModal(true);
            setSelectedRischio(rischio);
        }
    }

    const handleCreateNewRischio = () => {
        if (!getPermissions().canCreate) return;
        if (!selectedPrestazione) {
            enqueueSnackbar("Seleziona una prestazione", { variant: "warning" });
            return;
        }
        if (!selectedProfessionista) {
            enqueueSnackbar("Seleziona un professionista", { variant: "warning" });
            return;
        }

        // Crea un nuovo rischio vuoto
        const newRischio = createEmptyRischioModel();
        setSelectedRischio(newRischio);
        setAutoCreatedRischio(false); // Reset del flag per la creazione manuale
        
        enqueueSnackbar("Nuova valutazione creata. Completa i dati e salva.", {
            variant: "success",
        });
    };

    //#endregion

    //#region Utility Functions - Risk Model
    const createEmptyRischioModel = () => {
        // Calcola la data di controllo basandosi sulla data di valutazione selezionata
        const baseDate = dataValutazione || selectedPrestazione.dataStart;
        const dataControllo = new Date(baseDate);
        dataControllo.setMonth(dataControllo.getMonth() + 36); // Default 36 mesi

        // Consulenti del Lavoro: propone nella Tabella C i valori dell'autovalutazione di studio
        // (Ri1/Ri2/Ri3), poi modificabili per la singola valutazione. Se assente, default 1.
        const studioAutoval = studioAutovalRef.current;
        const spC1_1Livello = studioAutoval?.Ri1 ?? 1;
        const spC1_2Livello = studioAutoval?.Ri2 ?? 1;
        const spC1_3Livello = studioAutoval?.Ri3 ?? 1;

        const newRischio = new rischioModel(
            -1, // ID temporaneo per indicare che è un nuovo rischio
            baseDate, // Usa una copia della data di valutazione
            selectedPrestazione,
            1, // sp_a1_livello
            false, // sp_a1_1
            false, // sp_a1_2
            false, // sp_a1_3
            false, // sp_a1_4
            false, // sp_a1_5
            false, // sp_a1_6
            false, // sp_a1_7
            false, // sp_a1_altro
            "", // sp_a1_altro_note
            1, // sp_a2_livello
            false, // sp_a2_1
            false, // sp_a2_2
            false, // sp_a2_3
            false, // sp_a2_altro
            "", // sp_a2_altro_note
            1, // sp_a3_livello
            false, // sp_a3_1
            false, // sp_a3_2
            false, // sp_a3_3
            false, // sp_a3_4
            false, // sp_a3_altro
            "", // sp_a3_altro_note
            1, // sp_a4_livello
            false, // sp_a4_1
            false, // sp_a4_2
            false, // sp_a4_3
            false, // sp_a4_4
            false, // sp_a4_altro
            "", // sp_a4_altro_note
            4, // sp_a_subTotal
            1, // sp_b1_livello
            false, // sp_b1_1
            false, // sp_b1_2
            false, // sp_b1_3
            false, //sp_b1_4
            false, // sp_b1_altro
            "", // sp_b1_altro_note
            1, // sp_b2_livello
            false, // sp_b2_1
            false, // sp_b2_2
            false, // sp_b2_3
            false, // sp_b2_4
            false, // sp_b2_altro
            "", // sp_b2_altro_note
            1, // sp_b3_livello
            false, // sp_b3_1
            false, // sp_b3_2
            false, // sp_b3_3
            false, // sp_b3_altro
            "", // sp_b3_altro_note
            1, // sp_b4_livello
            false, // sp_b4_1
            false, // sp_b4_2
            false, // sp_b4_3
            false, // sp_b4_altro
            "", // sp_b4_altro_note
            1, // sp_b5_livello
            false, // sp_b5_1
            false, // sp_b5_2
            false, // sp_b5_3
            false, // sp_b5_altro
            "", // sp_b5_altro_note
            1, // sp_b6_livello
            false, // sp_b6_1
            false, // sp_b6_2
            false, // sp_b6_3
            false, // sp_b6_4
            false, // sp_b6_altro
            "", // sp_b6_altro_note
            1, // sp_b7_livello
            6, // sp_b_subTotal
            spC1_1Livello, // sp_c1_1_livello
            spC1_2Livello, // sp_c1_2_livello
            spC1_3Livello, // sp_c1_3_livello
            3, // sp_c_subTotal
            0, // ef_ri_total
            0, // ef_sp_total
            0, // ef_total
            1, // ef_adeguata_livello
            true, // hasTabellaB
            selectedProfessionista?.userId || "", // providedBy
            new Date(), // createdOn
            null, // modifiedOn
            "", // ef_sp_totalS
            "", // ef_totalS
            "", // ef_aef_adeguata_livelloS
            dataControllo, // dataControlloCostante - calcolata dalla data di valutazione
            selectedProfessionista?.userId || "", // createdBy
            "", // modifiedBy
            null, // dateEnd
            fascicoloHasTitEfPep // hasTitEffPep - imposta automaticamente se ci sono titolari effettivi PEP nel fascicolo
        );

        return newRischio;
    };

    //#region Modal Handlers
    const handleModalDeleteClose = () => {
        setShowDeleteModal(false);
        setSelectedRischio(null);
    };

    const handleModalDeleteOkClick = async () => {
        if (!selectedRischio || !getPermissions().canDelete) return;
        try {
            let deletionResponse = await rischiApi.DeleteRischioById(selectedRischio.id);
            if(deletionResponse) {
                enqueueSnackbar("Rischio eliminato con successo", { variant: "success" });
                setShowDeleteModal(false);
                setSelectedRischio(null);
                // Triggera il ricaricamento dei rischi
                setReloadTrigger(prev => prev + 1);

                //Aggiorno la data di ultima modifica del fascicolo
                fascicolo.tenantId = tenantId;
                fascicolo.aziendaId = aziendaId;
                let updatedFascicolo = await fascicoloApi.UpdateFascicolo(fascicolo,fascicolo.isPrivate);
                if(!updatedFascicolo){
                    enqueueSnackbar("Errore durante l'aggiornamento del fascicolo",{variant:"error"});
                }
            }
        } catch (error) {
            logApi.AddLog(error.message,error.stack,"handleModalDeleteOkClick","NewRischioForm");
            throw new Error(`Error deleting rischio: ${error.message}`);
        } finally {
            await reloadStatusFascicolo();
        }
    }
    //#endregion

    //#region Save Functions
    const handleSaveRischio = async (rischioData) => {
        const currentPermissions = getPermissions();
        if (!(rischioData.id === -1 ? currentPermissions.canCreate : currentPermissions.canUpdate)) return;
        try {
            setBackDropOpen(true)
            
            // Aggiorna la data del rischio con la data di valutazione selezionata dall'utente
            const updatedRischioData = {
                ...rischioData,
                date: new Date(new Date(rischioData.date).setHours(12,0,0,0))
            };
            
            if (updatedRischioData.id === -1) {
                // Nuovo rischio - aggiungi
                const response = await apiAddRischio.fetch({
                    prestazioneId: updatedRischioData.prestazione.id,
                    date: updatedRischioData.date,
                    spA1Livello: updatedRischioData.sp_a1_livello,
                    spA1_1: updatedRischioData.sp_a1_1,
                    spA1_2: updatedRischioData.sp_a1_2,
                    spA1_3: updatedRischioData.sp_a1_3,
                    spA1_4: updatedRischioData.sp_a1_4,
                    spA1_5: updatedRischioData.sp_a1_5,
                    spA1_6: updatedRischioData.sp_a1_6,
                    spA1_7: updatedRischioData.sp_a1_7,
                    spA1Altro: updatedRischioData.sp_a1_altro,
                    spA1AltroNote: updatedRischioData.sp_a1_altro_note || "",
                    spA2Livello: updatedRischioData.sp_a2_livello,
                    spA2_1: updatedRischioData.sp_a2_1,
                    spA2_2: updatedRischioData.sp_a2_2,
                    spA2_3: updatedRischioData.sp_a2_3,
                    spA2Altro: updatedRischioData.sp_a2_altro,
                    spA2AltroNote: updatedRischioData.sp_a2_altro_note || "",
                    spA3Livello: updatedRischioData.sp_a3_livello,
                    spA3_1: updatedRischioData.sp_a3_1,
                    spA3_2: updatedRischioData.sp_a3_2,
                    spA3_3: updatedRischioData.sp_a3_3,
                    spA3_4: updatedRischioData.sp_a3_4,
                    spA3Altro: updatedRischioData.sp_a3_altro,
                    spA3AltroNote: updatedRischioData.sp_a3_altro_note || "",
                    spA4Livello: updatedRischioData.sp_a4_livello,
                    spA4_1: updatedRischioData.sp_a4_1,
                    spA4_2: updatedRischioData.sp_a4_2,
                    spA4_3: updatedRischioData.sp_a4_3,
                    spA4_4: updatedRischioData.sp_a4_4,
                    spA4Altro: updatedRischioData.sp_a4_altro,
                    spA4AltroNote: updatedRischioData.sp_a4_altro_note || "",
                    spASubTotale: updatedRischioData.sp_a_subTotal,
                    spB1Livello: updatedRischioData.sp_b1_livello,
                    spB1_1: updatedRischioData.sp_b1_1,
                    spB1_2: updatedRischioData.sp_b1_2,
                    spB1_3: updatedRischioData.sp_b1_3,
                    spB1_4: updatedRischioData.sp_b1_4,
                    spB1Altro: updatedRischioData.sp_b1_altro,
                    spB1AltroNote: updatedRischioData.sp_b1_altro_note || "",
                    spB2Livello: updatedRischioData.sp_b2_livello,
                    spB2_1: updatedRischioData.sp_b2_1,
                    spB2_2: updatedRischioData.sp_b2_2,
                    spB2_3: updatedRischioData.sp_b2_3,
                    spB2_4: updatedRischioData.sp_b2_4,
                    spB2Altro: updatedRischioData.sp_b2_altro,
                    spB2AltroNote: updatedRischioData.sp_b2_altro_note || "",
                    spB3Livello: updatedRischioData.sp_b3_livello,
                    spB3_1: updatedRischioData.sp_b3_1,
                    spB3_2: updatedRischioData.sp_b3_2,
                    spB3_3: updatedRischioData.sp_b3_3,
                    spB3Altro: updatedRischioData.sp_b3_altro,
                    spB3AltroNote: updatedRischioData.sp_b3_altro_note || "",
                    spB4Livello: updatedRischioData.sp_b4_livello,
                    spB4_1: updatedRischioData.sp_b4_1,
                    spB4_2: updatedRischioData.sp_b4_2,
                    spB4_3: updatedRischioData.sp_b4_3,
                    spB4Altro: updatedRischioData.sp_b4_altro,
                    spB4AltroNote: updatedRischioData.sp_b4_altro_note || "",
                    spB5Livello: updatedRischioData.sp_b5_livello,
                    spB5_1: updatedRischioData.sp_b5_1,
                    spB5_2: updatedRischioData.sp_b5_2,
                    spB5_3: updatedRischioData.sp_b5_3,
                    spB5Altro: updatedRischioData.sp_b5_altro,
                    spB5AltroNote: updatedRischioData.sp_b5_altro_note || "",
                    spB6Livello: updatedRischioData.sp_b6_livello,
                    spB6_1: updatedRischioData.sp_b6_1,
                    spB6_2: updatedRischioData.sp_b6_2,
                    spB6_3: updatedRischioData.sp_b6_3,
                    spB6_4: updatedRischioData.sp_b6_4,
                    spB6Altro: updatedRischioData.sp_b6_altro,
                    spB6AltroNote: updatedRischioData.sp_b6_altro_note || "",
                    spB7Livello: updatedRischioData.sp_b7_livello || 1,
                    spBSubTotale: updatedRischioData.sp_b_subTotal,
                    spC1_1_Livello: updatedRischioData.sp_c1_1_livello,
                    spC1_2_Livello: updatedRischioData.sp_c1_2_livello,
                    spC1_3_Livello: updatedRischioData.sp_c1_3_livello,
                    spCSubTotale: updatedRischioData.sp_c_subTotal,
                    efRiTotale: updatedRischioData.ef_ri_total,
                    efSpTotale: updatedRischioData.ef_sp_total,
                    efTotale: updatedRischioData.ef_total,
                    efAdeguataLivello: updatedRischioData.ef_adeguata_livello,
                    hasTabellaB: updatedRischioData.hasTabellaB,
                    providedBy: updatedRischioData.providedBy,
                    dataControlloCostante: updatedRischioData.dataControlloCostante,
                    hasTitEffPep: updatedRischioData.hasTitEffPep,
                });
                
                if (!response.data.errors) {
                    enqueueSnackbar("Valutazione creata con successo", { variant: "success" });
                    // Aggiorna il selectedRischio con l'ID reale dal server
                    const newId = response.data.data.insert_rischio_one.Id;
                    const savedRischio = {...updatedRischioData, id: newId};
                    setSelectedRischio(savedRischio);
                    // Ricarica i rischi DOPO aver impostato il rischio selezionato
                    setReloadTrigger(prev => prev + 1);
                } else {
                    logApi.AddLog(response.data.errors,"handleSaveRischio - Add","NewRischioForm");
                    throw new Error("Errore nella creazione");
                }
            } else {
                // Rischio esistente - aggiorna
                const response = await apiUpdateRischio.fetch({
                    id: updatedRischioData.id,
                    prestazioneId: updatedRischioData.prestazione.id,  
                    date: updatedRischioData.date,
                    spA1Livello: updatedRischioData.sp_a1_livello,
                    spA1_1: updatedRischioData.sp_a1_1,
                    spA1_2: updatedRischioData.sp_a1_2,
                    spA1_3: updatedRischioData.sp_a1_3,
                    spA1_4: updatedRischioData.sp_a1_4,
                    spA1_5: updatedRischioData.sp_a1_5,
                    spA1_6: updatedRischioData.sp_a1_6,
                    spA1_7: updatedRischioData.sp_a1_7,
                    spA1Altro: updatedRischioData.sp_a1_altro,
                    spA1AltroNote: updatedRischioData.sp_a1_altro_note || "",
                    spA2Livello: updatedRischioData.sp_a2_livello,
                    spA2_1: updatedRischioData.sp_a2_1,
                    spA2_2: updatedRischioData.sp_a2_2,
                    spA2_3: updatedRischioData.sp_a2_3,
                    spA2Altro: updatedRischioData.sp_a2_altro,
                    spA2AltroNote: updatedRischioData.sp_a2_altro_note || "",
                    spA3Livello: updatedRischioData.sp_a3_livello,
                    spA3_1: updatedRischioData.sp_a3_1,
                    spA3_2: updatedRischioData.sp_a3_2,
                    spA3_3: updatedRischioData.sp_a3_3,
                    spA3_4: updatedRischioData.sp_a3_4,
                    spA3Altro: updatedRischioData.sp_a3_altro,
                    spA3AltroNote: updatedRischioData.sp_a3_altro_note || "",
                    spA4Livello: updatedRischioData.sp_a4_livello,
                    spA4_1: updatedRischioData.sp_a4_1,
                    spA4_2: updatedRischioData.sp_a4_2,
                    spA4_3: updatedRischioData.sp_a4_3,
                    spA4_4: updatedRischioData.sp_a4_4,
                    spA4Altro: updatedRischioData.sp_a4_altro,
                    spA4AltroNote: updatedRischioData.sp_a4_altro_note || "",
                    spASubTotale: updatedRischioData.sp_a_subTotal,
                    spB1Livello: updatedRischioData.sp_b1_livello,
                    spB1_1: updatedRischioData.sp_b1_1,
                    spB1_2: updatedRischioData.sp_b1_2,
                    spB1_3: updatedRischioData.sp_b1_3,
                    spB1_4: updatedRischioData.sp_b1_4,
                    spB1Altro: updatedRischioData.sp_b1_altro,
                    spB1AltroNote: updatedRischioData.sp_b1_altro_note || "",
                    spB2Livello: updatedRischioData.sp_b2_livello,
                    spB2_1: updatedRischioData.sp_b2_1,
                    spB2_2: updatedRischioData.sp_b2_2,
                    spB2_3: updatedRischioData.sp_b2_3,
                    spB2_4: updatedRischioData.sp_b2_4,
                    spB2Altro: updatedRischioData.sp_b2_altro,
                    spB2AltroNote: updatedRischioData.sp_b2_altro_note || "",
                    spB3Livello: updatedRischioData.sp_b3_livello,
                    spB3_1: updatedRischioData.sp_b3_1,
                    spB3_2: updatedRischioData.sp_b3_2,
                    spB3_3: updatedRischioData.sp_b3_3,
                    spB3Altro: updatedRischioData.sp_b3_altro,
                    spB3AltroNote: updatedRischioData.sp_b3_altro_note || "",
                    spB4Livello: updatedRischioData.sp_b4_livello,
                    spB4_1: updatedRischioData.sp_b4_1,
                    spB4_2: updatedRischioData.sp_b4_2,
                    spB4_3: updatedRischioData.sp_b4_3,
                    spB4Altro: updatedRischioData.sp_b4_altro,
                    spB4AltroNote: updatedRischioData.sp_b4_altro_note || "",
                    spB5Livello: updatedRischioData.sp_b5_livello,
                    spB5_1: updatedRischioData.sp_b5_1,
                    spB5_2: updatedRischioData.sp_b5_2,
                    spB5_3: updatedRischioData.sp_b5_3,
                    spB5Altro: updatedRischioData.sp_b5_altro,
                    spB5AltroNote: updatedRischioData.sp_b5_altro_note || "",
                    spB6Livello: updatedRischioData.sp_b6_livello,
                    spB6_1: updatedRischioData.sp_b6_1,
                    spB6_2: updatedRischioData.sp_b6_2,
                    spB6_3: updatedRischioData.sp_b6_3,
                    spB6_4: updatedRischioData.sp_b6_4,
                    spB6Altro: updatedRischioData.sp_b6_altro,
                    spB6AltroNote: updatedRischioData.sp_b6_altro_note || "",
                    spB7Livello: updatedRischioData.sp_b7_livello || 1,
                    spBSubTotale: updatedRischioData.sp_b_subTotal,
                    spC1_1_Livello: updatedRischioData.sp_c1_1_livello,
                    spC1_2_Livello: updatedRischioData.sp_c1_2_livello,
                    spC1_3_Livello: updatedRischioData.sp_c1_3_livello,
                    spCSubTotale: updatedRischioData.sp_c_subTotal,
                    efRiTotale: updatedRischioData.ef_ri_total,
                    efSpTotale: updatedRischioData.ef_sp_total,
                    efTotale: updatedRischioData.ef_total,
                    efAdeguataLivello: updatedRischioData.ef_adeguata_livello,
                    hasTabellaB: updatedRischioData.hasTabellaB,
                    providedBy: updatedRischioData.providedBy,
                    dataControlloCostante: updatedRischioData.dataControlloCostante,
                    hasTitEffPep: updatedRischioData.hasTitEffPep,
                });
                
                if (!response.data.errors) {
                    enqueueSnackbar("Valutazione aggiornata con successo", { variant: "success" });
                    // Ricarica i rischi
                    setReloadTrigger(prev => prev + 1);
                } else {
                    logApi.AddLog(response.data.errors,"handleSaveRischio - Update","NewRischioForm");
                    throw new Error("Errore nell'aggiornamento");
                }
            }

            //Aggiorno la data di ultima modifica del fascicolo
            fascicolo.tenantId = tenantId;
            fascicolo.aziendaId = aziendaId;
            let updatedFascicolo = await fascicoloApi.UpdateFascicolo(fascicolo,fascicolo.isPrivate);
            if(!updatedFascicolo){
                enqueueSnackbar("Errore durante l'aggiornamento del fascicolo",{variant:"error"});
                return;
            }
        } catch (error) {
            console.error("Errore nel salvataggio:", error);
            enqueueSnackbar("Errore nel salvataggio della valutazione", {
                variant: "error",
            });
            logApi.AddLog(error.message,error.stack,"handleSaveRischio","NewRischioForm");
        } finally {
            setBackDropOpen(false)
            await reloadStatusFascicolo();
        }
    };
    const handleCancel = () =>{
        setSelectedRischio(null)
    }
    //#endregion

    //#region Return Graphics
    if (!isDataLoaded) {
        return <Loading open={backDropOpen && isLoadingRischi} />;
    }
    return (
        <Fragment>
            <VaporPage.Section>
                {showDeleteModal && 
                    <ModalDelete
                        show={showDeleteModal}
                        onClose={handleModalDeleteClose}
                        onOk={handleModalDeleteOkClick}
                        deleteMessage={t("modal.deleteDesc")}
                    ></ModalDelete>
                }
                <Grid container spacing={3}>
                    {/* Colonna Sinistra - Filtri */}
                    <Stack direction="column" spacing={1} sx={{width:"25%"}}>
                        <Stack flexDirection="column" spacing={3}>
                            <Autocomplete
                            id="ddlPrestazioni"
                            key={uuidv4()}
                            size="small"
                            fullWidth
                            options={prestazioniList}
                            sx={{ width: "100%" }}
                            getOptionLabel={(option) => option.prestazioneNome}
                            defaultValue={
                                prestazioniList.filter(
                                (item) => item.id === selectedPrestazione?.id
                                )[0]
                            }
                            renderInput={(params) => (
                                <TextField
                                {...params}
                                label="Prestazione"
                                placeholder="Seleziona una prestazione"
                                required
                                />
                            )}
                            onChange={handlePrestazioneChange}
                            disabled={!!(prestazionePreselected && prestazionePreselected.id)}
                            />

                            <LocalizationProvider
                                dateAdapter={AdapterDateFns}
                                adapterLocale={it}
                            >
                            <DatePicker
                                label="Data di Valutazione"
                                    closeOnSelect
                                    format="dd/MM/yyyy"
                                    mask="__/__/____"
                                    minDate={selectedPrestazione && isDateValid(selectedPrestazione.dataStart)
                                        ? new Date(selectedPrestazione.dataStart)
                                        : null
                                    }
                                    maxDate={null}
                                    value={dataValutazione}
                                    slotProps={{
                                        textField: {
                                        placeholder: "dd/MM/yyyy",
                                        size: "small",
                                        fullWidth: true,
                                        },
                                    }}
                                    onChange={handleDataValutazioneChange}
                                    disabled={!selectedPrestazione || !(selectedRischio && selectedRischio.id !== -1 ? permissions.canUpdate : permissions.canCreate)}
                            />
                            </LocalizationProvider>

                            <Autocomplete
                            id="ddlUtenti"
                            key={uuidv4()}
                            size="small"
                            fullWidth
                            options={utentiList}
                            sx={{ width: "100%" }}
                            getOptionLabel={(option) => {
                                if (!option) return '';
                                return option.completeName || option.name || option.userId || '';
                            }}
                            value={selectedProfessionista}
                            defaultValue={
                                utentiList.filter(
                                (item) => item.userId === selectedProfessionista?.userId
                                )[0]
                            }
                            renderInput={(params) => (
                                <TextField
                                {...params}
                                label="Professionista"
                                placeholder={
                                    !selectedPrestazione 
                                        ? "Seleziona prima una prestazione" 
                                        : utentiList.length === 0 
                                            ? "Nessun professionista associato"
                                            : "Seleziona un professionista"
                                }
                                required
                                />
                            )}
                            onChange={handleProfessionistaChange}
                            disabled={(prestazionePreselected && prestazionePreselected.id) ? true : (!selectedPrestazione || utentiList.length === 0)}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                return option.userId === value.userId;
                            }}
                            />

                            <Divider  variant="standard" sx={{ pt: 1 }}/>
                            {/* Bottone per creare nuovo rischio */}
                            <Button
                                variant="contained"
                                size="small"
                                endIcon={<Add />}
                                onClick={handleCreateNewRischio}
                                disabled={!permissions.canCreate || !selectedPrestazione || utentiList.length === 0}
                            >
                                {t("valutazione.titoloAdd")}
                            </Button>

                            {rischiList.length > 0 ? (
                                <RischiList 
                                    rischiList={rischiList}
                                    selectedRischio={selectedRischio}
                                    utentiList={utentiList}
                                    selectedPrestazione={selectedPrestazione}
                                    onDeleteRischio={handleRischioDelete}
                                    onSelectRischio={handleRischioSelect}
                                />
                            ) : (
                                <NoData size="subtitle"/>
                            )}
                        </Stack>
                    </Stack>

                    <Stack direction="column" spacing={1} sx={{width:"75%"}}>
                        {selectedRischio && 
                            <RischioDetail 
                                selectedRischio={selectedRischio} 
                                selectedPrestazione={selectedPrestazione}
                                dataValutazione={dataValutazione}
                                onSaveRischio={handleSaveRischio}
                                onCancel={handleCancel}
                                getAdeguateVerificationRules={getAdeguateVerificationRules}
                                baseRischioGradoList={baseRischioGradoList}
                                rischioPaeseCliente={rischioPaeseCliente}
                                rischioProvinciaCliente={rischioProvinciaCliente}
                                rischioPaesePrest={rischioPaesePrest}
                                rischioProvinciaPrest={rischioProvinciaPrest}
                                fascicoloHasTitEfPep={fascicoloHasTitEfPep}
                            />
                        }   
                    </Stack>                                        
                </Grid>
            </VaporPage.Section>
        </Fragment>
    );
  //#endregion
};
